import * as React from "react";
import { useEffect, useState } from "react";
import {
  CountryCode,
  InfoDetails,
  SortOrder,
} from "@home-health-notify/domain";
import { Accordion, AccordionSummary, Typography } from "@mui/material";
import { InfoFieldForm } from "./InfoFieldForm";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  AccordionDetailsAddInfo,
  AddInfoLayout,
} from "../AddPatientGroup.style";
import { sortBy } from "lodash";

type AddInfoFieldProps = {
  title: string;
  infoKey: string;
  onAddInfoDataChange: (key: string, value: InfoDetails[]) => void;
  infoDetails: InfoDetails[];
  isExpanded?: boolean;
  initialInfoDetails?: InfoDetails[];
};
export const AddInfoField = ({
  title,
  infoKey,
  onAddInfoDataChange,
  infoDetails,
  initialInfoDetails,
  isExpanded,
}: AddInfoFieldProps) => {
  const defaultInfoDetail: InfoDetails = {
    name: "",
    countryCode: CountryCode.US,
    phoneNo: "",
    sortOrder: SortOrder.PRIMARY,
  };

  const defaultSortingOrder = () => {
    if (infoDetails && infoDetails.length > 0) {
      return infoDetails[0].sortOrder === SortOrder.SECONDARY ? 1 : 2;
    } else {
      return 2;
    }
  };

  type AddInfoFieldKeys = "addInfo1" | "addInfo2";
  const [addInfoFields, setAddInfoFields] = useState<
    Record<AddInfoFieldKeys, InfoDetails>
  >({
    addInfo1:
      initialInfoDetails && initialInfoDetails.length > 0
        ? sortBy(initialInfoDetails, "sortOrder")[0]
        : defaultInfoDetail,
    addInfo2:
      initialInfoDetails && initialInfoDetails.length > 1
        ? sortBy(initialInfoDetails, "sortOrder")[1]
        : { ...defaultInfoDetail, sortOrder: SortOrder.SECONDARY },
  });

  useEffect(() => {
    if (initialInfoDetails && initialInfoDetails.length > 0) {
      const sorted = sortBy(initialInfoDetails, "sortOrder");
      setAddInfoFields({
        addInfo1: sorted[0],
        addInfo2:
          initialInfoDetails.length > 1
            ? sorted[1]
            : { ...defaultInfoDetail, sortOrder: SortOrder.SECONDARY },
      });
    } else {
      setAddInfoFields({
        addInfo1: defaultInfoDetail,
        addInfo2: {
          ...defaultInfoDetail,
          sortOrder: defaultSortingOrder(),
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInfoDetails]);

  const setInfoData = (name: string, updatedInfoData: InfoDetails) => {
    const typedName = name as AddInfoFieldKeys;
    const addInfoData: InfoDetails[] = [];
    (Object.keys(addInfoFields) as AddInfoFieldKeys[]).forEach(fieldName => {
      if (typedName === fieldName) {
        setAddInfoFields({ ...addInfoFields, [fieldName]: updatedInfoData });
        addInfoData.push(updatedInfoData);
      } else {
        addInfoData.push(addInfoFields[fieldName]);
      }
    });
    onAddInfoDataChange(infoKey, addInfoData);
  };

  const onSetAddInfoFields = (name: string, value: number) => {
    const typedName = name as AddInfoFieldKeys;
    let clonedInfoFields = { ...addInfoFields };
    const isPrimary = value === 1;
    (Object.keys(clonedInfoFields) as AddInfoFieldKeys[]).forEach(typeName => {
      if (typeName === typedName) {
        clonedInfoFields[typeName].sortOrder = isPrimary ? 1 : 2;
      } else {
        clonedInfoFields[typeName].sortOrder = isPrimary ? 2 : 1;
      }
    });

    setAddInfoFields(clonedInfoFields);
    (Object.keys(clonedInfoFields) as AddInfoFieldKeys[]).forEach(typeName => {
      setInfoData(typeName, clonedInfoFields[typeName]);
    });
  };

  const [expanded, setExpanded] = React.useState<string | false>(false);

  useEffect(() => {
    setExpanded(isExpanded ? "panel1" : false);
  }, [isExpanded]);

  const handleChangeAccordion =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <AddInfoLayout>
      <Accordion
        expanded={expanded === "panel1"}
        onChange={handleChangeAccordion("panel1")}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
        >
          <Typography>{title}</Typography>
        </AccordionSummary>
        <AccordionDetailsAddInfo>
          {Object.keys(addInfoFields).map(name => {
            const typedName = name as AddInfoFieldKeys;
            return (
              <InfoFieldForm
                infoKey={infoKey}
                name={typedName}
                key={typedName}
                onSetFormData={setInfoData}
                onSetAddInfoFields={onSetAddInfoFields}
                infoDetail={addInfoFields[typedName]}
              />
            );
          })}
        </AccordionDetailsAddInfo>
      </Accordion>
    </AddInfoLayout>
  );
};
